import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import { Formik } from "formik";
import * as Yup from "yup";
import { post } from "aws-amplify/api";
import LoaderButton from "../components/LoaderButton";
import StepUpDialog from "../components/StepUpDialog";

const CATEGORY_OPTIONS = ["Standard", "Trial", "Enterprise"];

const LICENSE_ID_RE = /^[a-z0-9][a-z0-9._-]{1,62}$/;
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&’'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const schema = Yup.object({
  licenseId: Yup.string()
    .required("Required")
    .matches(LICENSE_ID_RE, "Lowercase letters, digits, dot, dash, or underscore (2–63 chars)."),
  licensedTo: Yup.string().trim().required("Required"),
  licenseEndDate: Yup.string()
    .required("Required")
    .matches(/^\d{4}-\d{2}-\d{2}$/, "Use yyyy-MM-dd"),
  assignedLicenses: Yup.number()
    .typeError("Must be a number")
    .integer("Must be an integer")
    .min(1, "Must be at least 1")
    .required("Required"),
  category: Yup.string().required("Required"),
  selfRegistration: Yup.boolean(),
  emailDomains: Yup.array()
    .of(Yup.string().matches(DOMAIN_RE, "Invalid domain"))
    .min(1, "At least one email domain is required"),
  adminEmail: Yup.string()
    .required("Required")
    .matches(EMAIL_RE, "Invalid email address"),
  adminFirstName: Yup.string(),
  adminLastName: Yup.string()
});

const initialValues = {
  licenseId: "",
  licensedTo: "",
  licenseEndDate: "",
  assignedLicenses: 5,
  category: "Standard",
  selfRegistration: false,
  emailDomains: [],
  adminEmail: "",
  adminFirstName: "",
  adminLastName: ""
};

async function readErrorBody(error) {
  // Amplify v6 parks the response body in different places depending on
  // whether buildRestApiError managed to parse it (when it can't, it
  // wraps the original error as `UnknownError` and the body lives on
  // .underlyingError or .cause). Walk every plausible slot.
  const candidates = [
    error?.response?.body,
    error?.response,
    error?.underlyingError?.response?.body,
    error?.cause?.response?.body,
    error?.body,
  ];

  const tryRead = async (src) => {
    if (src == null) return null;
    // Already a plain object envelope.
    if (typeof src === "object" && (src.error || src.detail || src.reference)) {
      return src;
    }
    // Stream-like with .json() — guard with a 1s timeout so we never hang.
    if (typeof src?.json === "function") {
      try {
        return await Promise.race([
          src.json(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
      } catch (_) { /* fall through */ }
    }
    // Stream-like with .text() — sometimes amplify gives us this.
    if (typeof src?.text === "function") {
      try {
        const text = await Promise.race([
          src.text(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
        if (typeof text === "string" && text) return JSON.parse(text);
      } catch (_) { /* fall through */ }
    }
    // Raw string body.
    if (typeof src === "string" && src) {
      try { return JSON.parse(src); } catch (_) { return null; }
    }
    return null;
  };

  for (const c of candidates) {
    const parsed = await tryRead(c);
    if (parsed) return parsed;
  }
  return null;
}

export default function CreateOrganization() {
  const navigate = useNavigate();
  const [alertMessage, setAlertMessage] = useState("");
  const [result, setResult] = useState(null);   // { licenseId, licensedTo, alreadyExisted, firstAdmin }
  const [pendingRetry, setPendingRetry] = useState(null);

  const handleSubmit = async (values, { setSubmitting }) => {
    setAlertMessage("");
    try {
      const op = post({
        apiName: "license",
        path: "/organizations",
        options: {
          body: {
            licenseId: values.licenseId.trim().toLowerCase(),
            licensedTo: values.licensedTo.trim(),
            licenseEndDate: values.licenseEndDate,
            assignedLicenses: Number(values.assignedLicenses),
            category: values.category,
            selfRegistration: Boolean(values.selfRegistration),
            emailDomains: values.emailDomains.map((d) => d.trim().toLowerCase()),
            firstAdmin: {
              email: values.adminEmail.trim().toLowerCase(),
              firstName: values.adminFirstName.trim(),
              lastName: values.adminLastName.trim()
            }
          }
        }
      });
      const { body } = await op.response;
      const created = await body.json();
      setResult(created);
    } catch (e) {
      const errorBody = await readErrorBody(e);

      // Step-up required: stash a retry closure for after the dialog,
      // and don't show an error alert (the dialog speaks for itself).
      if (errorBody?.error === "STEP_UP_REQUIRED") {
        setPendingRetry(() => () => handleSubmit(values, { setSubmitting: () => {} }));
        setSubmitting(false);
        return;
      }

      // eslint-disable-next-line no-console
      console.error("create_organization failed", { errorBody, error: e });

      let message;
      if (errorBody?.error === "LICENSE_PARAMS_MISMATCH") {
        const conflicts = (errorBody.conflicts ?? [])
          .map((c) => `${c.field}: persisted "${c.persisted}", requested "${c.requested}"`)
          .join("; ");
        message = `A licence with this id already exists with different values${conflicts ? ` (${conflicts})` : ""}. Use the licences page to edit it, or pick a different id.`;
      } else if (errorBody?.error === "DOMAIN_TAKEN") {
        message = `Email domain '${errorBody.domain}' is already mapped to licence '${errorBody.owner}'.`;
      } else if (errorBody?.error === "ADMIN_USER_IN_OTHER_LICENSE") {
        message = errorBody.detail ?? "That admin email already belongs to a different licence.";
      } else if (errorBody?.detail) {
        message = errorBody.detail;
      } else if (errorBody?.error) {
        const ref = errorBody.reference ? ` · ref ${errorBody.reference}` : "";
        message = `${errorBody.error}${ref}`;
      } else if (e?.message && e.message !== "Unknown error") {
        message = e.message;
      } else {
        message = "Failed to create organisation. Open the devtools console for details.";
      }
      setAlertMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            {result.alreadyExisted ? "Organisation already existed" : "Organisation created"}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {result.alreadyExisted
            ? "A licence with this id was already provisioned. Existing keys, groups, and admin user were re-used; nothing new was created."
            : `Licence ${result.licenseId} provisioned. The first admin (${result.firstAdmin?.email}) has been emailed an invite to set their password.`}
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="overline" color="text.secondary">Licence</Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              <Row label="Licence id" value={result.licenseId} />
              <Row label="Licensed to" value={result.licensedTo} />
              <Row label="End date" value={result.licenseEndDate} />
              <Row label="Assigned licences" value={result.assignedLicenses} />
              <Row label="Category" value={result.category} />
              <Row label="Self-registration" value={result.selfRegistration ? "On" : "Off"} />
              <Row label="Email domains" value={(result.emailDomains ?? []).join(", ")} />
            </Stack>
            {result.steps ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary">Provisioning steps</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {Object.entries(result.steps).map(([step, outcome]) => (
                    <Chip
                      key={step}
                      label={`${step}: ${typeof outcome === "string" ? outcome : "applied"}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </>
            ) : null}
          </CardContent>
        </Card>

        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" onClick={() => navigate("/admin/licenses")}>
            Back to licences
          </Button>
          <Button onClick={() => { setResult(null); setAlertMessage(""); }}>
            Create another
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
        New organisation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Provisions a licence, signs the V2 keys, creates the Cognito groups, and invites the first admin user.
      </Typography>

      {alertMessage ? <Alert severity="error" sx={{ mb: 3 }}>{alertMessage}</Alert> : null}

      <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Organisation</Typography>
                  <Stack spacing={2.5} sx={{ mt: 1.5 }}>
                    <TextField
                      name="licenseId"
                      label="Licence id"
                      placeholder="acmecorp"
                      helperText={touched.licenseId && errors.licenseId ? errors.licenseId : "Lowercase, no spaces. Stored as the Cognito group name."}
                      value={values.licenseId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.licenseId && errors.licenseId)}
                      fullWidth
                      required
                    />
                    <TextField
                      name="licensedTo"
                      label="Licensed to"
                      placeholder="Acme Corp"
                      helperText={touched.licensedTo && errors.licensedTo ? errors.licensedTo : "Free-text display name baked into the signed key."}
                      value={values.licensedTo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.licensedTo && errors.licensedTo)}
                      fullWidth
                      required
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        name="licenseEndDate"
                        label="End date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        helperText={touched.licenseEndDate && errors.licenseEndDate ? errors.licenseEndDate : ""}
                        value={values.licenseEndDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.licenseEndDate && errors.licenseEndDate)}
                        sx={{ flex: 1 }}
                        required
                      />
                      <TextField
                        name="assignedLicenses"
                        label="Assigned licences"
                        type="number"
                        inputProps={{ min: 1 }}
                        helperText={touched.assignedLicenses && errors.assignedLicenses ? errors.assignedLicenses : "Maximum number of users."}
                        value={values.assignedLicenses}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.assignedLicenses && errors.assignedLicenses)}
                        sx={{ flex: 1 }}
                        required
                      />
                    </Stack>
                    <TextField
                      select
                      name="category"
                      label="Category"
                      value={values.category}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                    >
                      {CATEGORY_OPTIONS.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </TextField>
                    <FormControlLabel
                      control={
                        <Switch
                          name="selfRegistration"
                          checked={Boolean(values.selfRegistration)}
                          onChange={handleChange}
                        />
                      }
                      label="Allow self-registration from licensed email domains"
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Email domains</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                    Users with these email domains will be mapped to this licence. Press Enter or comma after each domain.
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={values.emailDomains}
                    onChange={(_, v) => setFieldValue("emailDomains", v.map((d) => d.trim().toLowerCase()).filter(Boolean))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={values.emailDomains.length === 0 ? "acme.com" : ""}
                        error={Boolean(touched.emailDomains && errors.emailDomains)}
                        helperText={touched.emailDomains && errors.emailDomains ? String(errors.emailDomains) : ""}
                        onBlur={handleBlur}
                        name="emailDomains"
                        // Stop the browser from autofilling this with the
                        // saved email address — `name` contains "email".
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "new-password",
                          "data-form-type": "other"
                        }}
                      />
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">First admin</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                    The first user for this licence. They'll be a member of {`{licenseId}.admin`} and will receive an email invite to set a password.
                  </Typography>
                  <Stack spacing={2.5}>
                    <TextField
                      name="adminEmail"
                      label="Email"
                      placeholder="alice@acme.com"
                      helperText={touched.adminEmail && errors.adminEmail ? errors.adminEmail : ""}
                      value={values.adminEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.adminEmail && errors.adminEmail)}
                      fullWidth
                      required
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        name="adminFirstName"
                        label="First name"
                        value={values.adminFirstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        name="adminLastName"
                        label="Last name"
                        value={values.adminLastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button onClick={() => navigate(-1)} disabled={isSubmitting}>Cancel</Button>
                <LoaderButton
                  type="submit"
                  isLoading={isSubmitting}
                  variant="contained"
                  text="Create organisation"
                  loadingText="Creating…"
                />
              </Stack>
            </Stack>
          </form>
        )}
      </Formik>

      <StepUpDialog
        open={Boolean(pendingRetry)}
        onClose={() => setPendingRetry(null)}
        onVerified={async () => {
          const fn = pendingRetry;
          setPendingRetry(null);
          if (fn) await fn();
        }}
      />
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: "baseline" }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}
